"use client";

import { FilterOptions } from "@/@types";
import { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import { ROUTES } from "@/config/routes";
import Image from "next/image";

type Props = {
  filters: FilterOptions;
  unitType: "metric" | "english";
  setUnitType: (type: "metric" | "english") => void;
  selectedIndustry?: string;
  selectedFamily?: string;
  selectedSubfamily?: string;
  selectedBrand?: string;
  isMenuOpen?: boolean;
  toggleMenu?: () => void;
};

export default function ProductsFiltersCard({
  filters,
  unitType,
  setUnitType,
  selectedIndustry = "",
  selectedFamily = "",
  selectedSubfamily = "",
  selectedBrand = "",
  isMenuOpen = false,
  toggleMenu = () => { },
}: Props) {
  const router = useRouter();

  const [industry, setIndustry] = useState<string>(selectedIndustry);
  const [family, setFamily] = useState<string>(selectedFamily);
  const [subfamily, setSubfamily] = useState<string>(selectedSubfamily);
  const [brand, setBrand] = useState<string>(selectedBrand);

  const [isMobile, setIsMobile] = useState(false);
  const [showMenuIcon, setShowMenuIcon] = useState(false);

  const navigateToFilters = useCallback(
    (ind?: string, fam?: string, sub?: string, br?: string) => {
      if (br) {
        if (sub) {
          if (ind && fam) {
            return router.replace(ROUTES.brands.subfamily(br, ind, fam, sub));
          }
          if (ind) {
            return router.replace(ROUTES.brands.industry(br, ind));
          }
          return router.replace(ROUTES.brands.brand(br));
        }
        if (ind && fam) {
          return router.replace(ROUTES.brands.family(br, ind, fam));
        }
        if (ind) {
          return router.replace(ROUTES.brands.industry(br, ind));
        }
        return router.replace(ROUTES.brands.brand(br));
      }

      if (sub) return router.replace(ROUTES.products.subfamily(ind, fam, sub));
      if (fam) return router.replace(ROUTES.products.family(ind, fam));
      if (ind) return router.replace(ROUTES.products.industry(ind));
      router.replace(ROUTES.products.root);
    },
    [router]
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isMobile && isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [isMobile, isMenuOpen]);

  useEffect(() => {
    if (industry) {
      const validFamiliesSlugs = filters.families.map((fam) => fam.slug);
      if (family && !validFamiliesSlugs.includes(family)) {
        setFamily(validFamiliesSlugs.length > 0 ? validFamiliesSlugs[0] : "");
      }
    } else {
      setFamily("");
    }
  }, [industry, family, filters.families]);

  useEffect(() => {
    if (family) {
      const validSubfamiliesSlugs = filters.subfamilies.map((sub) => sub.slug);
      if (subfamily && !validSubfamiliesSlugs.includes(subfamily)) {
        setSubfamily(
          validSubfamiliesSlugs.length > 0 ? validSubfamiliesSlugs[0] : ""
        );
      }
    } else {
      setSubfamily("");
    }
  }, [family, subfamily, filters.subfamilies]);

  useEffect(() => {
    if (!isMobile) {
      navigateToFilters(industry, family, subfamily, brand);
    }
  }, [industry, family, subfamily, brand, isMobile, navigateToFilters]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShowMenuIcon(true);
      } else {
        setShowMenuIcon(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleRestore = () => {
    setIndustry("");
    setFamily("");
    setSubfamily("");
    setBrand("");
    if (!isMobile) {
      router.replace(ROUTES.products.root);
    }
  };

  const handleApplyMobile = () => {
    navigateToFilters(industry, family, subfamily, brand);
    toggleMenu();
  };

  const renderFiltersContent = () => (
    <>
      <div className="grid grid-cols-2">
        <button
          className={`uppercase text-sm font-medium py-2 px-4 border border-black rounded-sm rounded-r-none ${unitType === "english"
            ? "bg-black text-white"
            : "hover:bg-black hover:text-white"
            }`}
          onClick={() => setUnitType("english")}
        >
          EE.UU
        </button>
        <button
          className={`uppercase text-sm font-medium py-2 px-4 border border-l-0 border-black rounded-sm rounded-l-none ${unitType === "metric"
            ? "bg-black text-white"
            : "hover:bg-black hover:text-white"
            }`}
          onClick={() => setUnitType("metric")}
        >
          Métricas
        </button>
      </div>

      {filters.industries.length > 0 && (
        <div className="mb-4 mt-4">
          <h3 className="font-semibold text-gray-700">Industrias</h3>
          <div
            className={`space-y-2 ${filters.industries.length > 5 ? "max-h-40 overflow-y-auto" : ""
              }`}
          >
            {filters.industries.map((ind) => (
              <label
                key={ind.id}
                className="flex items-center space-x-2 cursor-pointer"
                onClick={() => setIndustry(ind.slug)}
              >
                <input
                  type="checkbox"
                  checked={industry === ind.slug}
                  className="hidden peer"
                  readOnly
                />
                <div className="w-3 h-3 border-2 border-gray-400 rounded-sm peer-checked:bg-yellow-400"></div>
                <p className="text-sm text-gray-400 truncate">{ind.title}</p>
              </label>
            ))}
          </div>
        </div>
      )}

      {industry && filters.families.length > 0 && (
        <div className="mb-4">
          <h3 className="font-semibold text-gray-700">Familias</h3>
          <div
            className={`space-y-2 ${filters.families.length > 5 ? "max-h-40 overflow-y-auto" : ""
              }`}
          >
            {filters.families.map((fam) => (
              <label
                key={fam.id}
                className="flex items-center space-x-2 cursor-pointer"
                onClick={() => setFamily(fam.slug)}
              >
                <input
                  type="checkbox"
                  checked={family === fam.slug}
                  className="hidden peer"
                  readOnly
                />
                <div className="w-3 h-3 border-2 border-gray-400 rounded-sm peer-checked:bg-yellow-400"></div>
                <p className="text-sm text-gray-400 truncate">{fam.title}</p>
              </label>
            ))}
          </div>
        </div>
      )}

      {family && filters.subfamilies.length > 0 && (
        <div className="mb-4">
          <h3 className="font-semibold text-gray-700">Subfamilias</h3>
          <div
            className={`space-y-2 ${filters.subfamilies.length > 5 ? "max-h-40 overflow-y-auto" : ""
              }`}
          >
            {filters.subfamilies.map((sub) => (
              <label
                key={sub.id}
                className="flex items-center space-x-2 cursor-pointer"
                onClick={() => setSubfamily(sub.slug)}
              >
                <input
                  type="checkbox"
                  checked={subfamily === sub.slug}
                  className="hidden peer"
                  readOnly
                />
                <div className="w-3 h-3 border-2 border-gray-400 rounded-sm peer-checked:bg-yellow-400"></div>
                <p className="text-sm text-gray-400 truncate">{sub.title}</p>
              </label>
            ))}
          </div>
        </div>
      )}

      {filters.brands.length > 0 && (
        <div className="mb-4">
          <h3 className="font-semibold text-gray-700">Marcas</h3>
          <div
            className={`space-y-2 ${filters.brands.length > 5 ? "max-h-40 overflow-y-auto" : ""
              }`}
          >
            {filters.brands.map((br) => (
              <label
                key={br.id}
                className="flex items-center space-x-2 cursor-pointer"
                onClick={() => setBrand(br.slug)}
              >
                <input
                  type="checkbox"
                  checked={brand === br.slug}
                  className="hidden peer"
                  readOnly
                />
                <div className="w-3 h-3 border-2 border-gray-400 rounded-sm peer-checked:bg-yellow-400"></div>
                <p className="text-sm text-gray-400 truncate">{br.title}</p>
              </label>
            ))}
          </div>
        </div>
      )}

      {isMobile ? (
        <div className="flex justify-center items-center gap-4 mt-6">
          <button
            className="flex items-center gap-2 py-2 px-4 bg-gray-200 text-gray-700 hover:text-white transition uppercase text-sm"
            onClick={handleRestore}
          >
            <Image
              src="/images/icons/restoreIcon.svg"
              alt="restaurar"
              width={15}
              height={15}
            />
            Restaurar
          </button>
          <button
            className="py-2 px-4 bg-black text-white uppercase text-sm font-medium rounded"
            onClick={handleApplyMobile}
          >
            Aplicar
          </button>
        </div>
      ) : (
        <button
          className="flex items-center gap-2 w-max uppercase mt-4 py-2 px-4 bg-gray-200 text-gray-700 hover:text-white transition"
          onClick={handleRestore}
        >
          <Image
            src="/images/icons/restoreIcon.svg"
            alt="restaurar"
            width={15}
            height={15}
          />
          Restaurar
        </button>
      )}
    </>
  );

  if (isMobile) {
    return (
      <div className="relative z-61">
        {!isMenuOpen && showMenuIcon && (
          <button
            onClick={toggleMenu}
            className="fixed bottom-5 left-5 bg-yellow-500 text-white p-4 rounded-full shadow-lg"
          >
            <Image
              src="/assets/icons/filter.svg"
              alt="Filtros"
              width={20}
              height={20}
              className="h-5 w-5"
            />
          </button>
        )}

        {isMenuOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex justify-center items-end z-50"
            onClick={toggleMenu}
          >
            <div
              className="w-full bg-white p-6 rounded-t-2xl shadow-lg transform transition-transform translate-y-0 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Filtros</h3>
                <button
                  onClick={toggleMenu}
                  className="text-gray-500 hover:text-black font-bold text-2xl"
                >
                  ✕
                </button>
              </div>
              {renderFiltersContent()}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-4 bg-white">
      {renderFiltersContent()}
    </div>
  );
}
